import * as React from "react";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import { forgotPassword, resetPassword } from "../../http/auth";
import { useDispatch } from "react-redux";
import { showToast } from "../../store/slices/common";
import { useNavigate } from "react-router-dom";
import routePaths from "../../router/route-paths";

export default function ForgotPassword() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [email, setEmail] = React.useState("");
  const [otp, setOtp] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [emailSent, setEmailSent] = React.useState(false);
  const [resendTimer, setResendTimer] = React.useState(0);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!emailSent) {
      try {
        const res = await forgotPassword({ email });
        if (res.status === 200) {
          setEmailSent(true);
          dispatch(
            showToast({ color: "success", msg: "OTP sent successfully" })
          );
          setResendTimer(60);
          const timer = setInterval(() => {
            setResendTimer((prev) => {
              if (prev === 1) clearInterval(timer);
              return prev - 1;
            });
          }, 1000);
        }
      } catch (error) {
        dispatch(
          showToast({
            color: "error",
            msg: "Failed to send OTP. Please try again.",
          })
        );
        console.log(error);
      }
    } else {
      try {
        const res = await resetPassword({
          email,
          password,
          otp: parseInt(otp),
        });
        console.log(res, "testhk");
        if (res.status === 200) {
          dispatch(
            showToast({ color: "success", msg: "Password reset successfully" })
          );
          navigate(routePaths.HOME);
        }
      } catch (error) {
        dispatch(
          showToast({
            color: "error",
            msg: "Invalid OTP or password. Please try again.",
          })
        );
        console.log(error);
      }
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    if (name === "email") setEmail(value);
    if (name === "otp") setOtp(value);
    if (name === "password") setPassword(value);
  };

  const onClickBack = () => {
    navigate(routePaths.HOME);
  };

  const handleResend = async () => {
    try {
      const res = await forgotPassword({ email });
      if (res.status === 200) {
        dispatch(
          showToast({ color: "success", msg: "OTP resent successfully" })
        );
        setResendTimer(60);
        const timer = setInterval(() => {
          setResendTimer((prev) => {
            if (prev === 1);
            return prev - 1;
          });
        }, 1000);
      }
    } catch (error) {
      dispatch(
        showToast({
          color: "error",
          msg: "Failed to resend OTP. Please try again.",
        })
      );
      console.log(error);
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <CssBaseline />
      <Box
        sx={{
          paddingTop: 8,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Avatar sx={{ m: 1, bgcolor: "secondary.main" }}>
          <LockOutlinedIcon />
        </Avatar>
        <Typography component="h1" variant="h5">
          {emailSent ? "Reset Password" : "Forgot Password"}
        </Typography>
        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
          {!emailSent ? (
            <>
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={handleChange}
                sx={{
                  "& input": {
                    fontSize: "18px",
                    padding: "14px",
                    color: "#343434",
                  },
                  "& .MuiFormLabel-root.Mui-focused": { color: "#0832DE" },
                  "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#0832DE !important",
                  },
                }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={!email}
                sx={{
                  mt: 3,
                  mb: 2,
                  background: "#0832DE",
                  borderRadius: "8px",
                  "&:hover": { background: "#0832DE" },
                }}
              >
                Send OTP
              </Button>
            </>
          ) : (
            <>
              <TextField
                margin="normal"
                required
                fullWidth
                id="otp"
                label="OTP"
                name="otp"
                autoComplete="off"
                autoFocus
                value={otp}
                onChange={handleChange}
                sx={{
                  "& input": {
                    fontSize: "18px",
                    padding: "14px",
                    color: "#343434",
                  },
                  "& .MuiFormLabel-root.Mui-focused": { color: "#0832DE" },
                  "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#0832DE !important",
                  },
                }}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                id="password"
                label="New Password"
                name="password"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={handleChange}
                sx={{
                  "& input": {
                    fontSize: "18px",
                    padding: "14px",
                    color: "#343434",
                  },
                  "& .MuiFormLabel-root.Mui-focused": { color: "#0832DE" },
                  "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#0832DE !important",
                  },
                }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={!otp || !password}
                sx={{
                  mt: 3,
                  mb: 2,
                  background: "#0832DE",
                  borderRadius: "8px",
                  "&:hover": { background: "#0832DE" },
                }}
              >
                Reset Password
              </Button>
              <Button
                onClick={handleResend}
                fullWidth
                variant="text"
                disabled={resendTimer > 0}
                sx={{
                  mt: 1,
                  color: resendTimer > 0 ? "#ccc" : "#0832DE",
                  "&:hover": { background: "transparent" },
                }}
              >
                Resend OTP {resendTimer > 0 && `(${resendTimer}s)`}
              </Button>
            </>
          )}
        </Box>
        <Typography
          sx={{
            color: "#67697A",
            fontSize: "14px",
            width: "100%",
            fontWeight: "500",
            textAlign: "center",
          }}
        >
          Back to{" "}
          <span
            style={{ color: "#0832DE", cursor: "pointer", fontWeight: "600" }}
            onClick={onClickBack}
          >
            Sign In
          </span>
        </Typography>
      </Box>
    </Container>
  );
}
